import { IconSymbolName } from '@/components/ui/icon-symbol';
import { ScenarioType } from '@/types/scenario';

export const SCENARIO_TYPES = ['internationalInvoicing', 'localPartner', 'localSubsidiary', 'alsoThere'] as const;

export const CONCEPTS: { key: Concept; i18nKey: string }[] = [
  { key: 'infrastructure', i18nKey: 'concepts.infrastructure' },
  { key: 'licenses', i18nKey: 'concepts.licenses' },
  { key: 'service', i18nKey: 'concepts.service' },
  { key: 'support', i18nKey: 'concepts.support' },
];

export type Concept = 'infrastructure' | 'licenses' | 'service' | 'support';

export const MAX_BENCHMARKING_ITEMS = 8;

export enum ResumeColor {
  GREEN = '#22C55E',
  RED = 'red',
  YELLOW = '#FACC15',
}

export interface ResumeItemData {
  resume: { value: number; color: ResumeColor };
  items?: { label: string; value: number; color: ResumeColor }[];
}

interface ScenarioResumeConfig {
  badge: {
    risk: { color: string; iconName: IconSymbolName };
    competitiveness: { color: string; iconName: IconSymbolName };
  };
  setupAndOperationalCosts: ResumeItemData;
  scability: ResumeItemData;
  timeToMarket: ResumeItemData;
  transactionalAndPaymentCapabilities: ResumeItemData;
  customerRelationship: ResumeItemData;
  businessAutonomy: ResumeItemData;
  compliance: ResumeItemData;
}

export const RESUME_CONFIGS: Record<ScenarioType, ScenarioResumeConfig> = {
  localPartner: {
    badge: {
      risk: { color: '#E0BC00', iconName: 'exclamationmark.triangle.fill' },
      competitiveness: { color: '#68BA7F', iconName: 'bar-chart' },
    },
    setupAndOperationalCosts: {
      resume: { value: 50, color: ResumeColor.YELLOW },
      items: [
        { label: 'setupCost', value: 45, color: ResumeColor.YELLOW },
        { label: 'operationalCost', value: 30, color: ResumeColor.YELLOW },
      ],
    },
    scability: {
      resume: { value: 50, color: ResumeColor.YELLOW },
      items: [
        { label: 'presence', value: 60, color: ResumeColor.YELLOW },
        { label: 'scalability', value: 40, color: ResumeColor.YELLOW },
      ],
    },
    timeToMarket: {
      resume: { value: 50, color: ResumeColor.YELLOW },
    },
    transactionalAndPaymentCapabilities: {
      resume: { value: 80, color: ResumeColor.GREEN },
      items: [
        { label: 'localInvoicingBenefits', value: 2, color: ResumeColor.GREEN },
        { label: 'localCurrencyPayments', value: 2, color: ResumeColor.GREEN },
        { label: 'consolidation', value: 1, color: ResumeColor.RED },
      ],
    },
    customerRelationship: {
      resume: { value: 30, color: ResumeColor.RED },
      items: [
        { label: 'localizedContacts', value: 3, color: ResumeColor.GREEN },
        { label: 'clientRelationship', value: 1, color: ResumeColor.RED },
      ],
    },
    businessAutonomy: {
      resume: { value: 20, color: ResumeColor.RED },
      items: [
        { label: 'operationalIndependence', value: 1, color: ResumeColor.RED },
        { label: 'retainControl', value: 1, color: ResumeColor.RED },
      ],
    },
    compliance: {
      resume: { value: 100, color: ResumeColor.GREEN },
    },
  },
  internationalInvoicing: {
    badge: {
      risk: { color: '#E0BC00', iconName: 'exclamationmark.triangle.fill' },
      competitiveness: { color: '#bc0000', iconName: 'bar-chart' },
    },
    setupAndOperationalCosts: {
      resume: { value: 5, color: ResumeColor.GREEN },
      items: [
        { label: 'setupCost', value: 5, color: ResumeColor.GREEN },
        { label: 'operationalCost', value: 5, color: ResumeColor.GREEN },
      ],
    },
    scability: {
      resume: { value: 5, color: ResumeColor.RED },
      items: [
        { label: 'presence', value: 5, color: ResumeColor.RED },
        { label: 'scalability', value: 5, color: ResumeColor.RED },
      ],
    },
    timeToMarket: {
      resume: { value: 5, color: ResumeColor.GREEN },
    },
    transactionalAndPaymentCapabilities: {
      resume: { value: 22, color: ResumeColor.RED },
      items: [
        { label: 'localInvoicingBenefits', value: 1, color: ResumeColor.RED },
        { label: 'localCurrencyPayments', value: 1, color: ResumeColor.RED },
        { label: 'consolidation', value: 1, color: ResumeColor.RED },
      ],
    },
    customerRelationship: {
      resume: { value: 79, color: ResumeColor.GREEN },
      items: [
        { label: 'localizedContacts', value: 1, color: ResumeColor.RED },
        { label: 'clientRelationship', value: 3, color: ResumeColor.GREEN },
      ],
    },
    businessAutonomy: {
      resume: { value: 82, color: ResumeColor.GREEN },
      items: [
        { label: 'operationalIndependence', value: 3, color: ResumeColor.GREEN },
        { label: 'retainControl', value: 2, color: ResumeColor.GREEN },
      ],
    },
    compliance: {
      resume: { value: 30, color: ResumeColor.RED },
    },
  },
  localSubsidiary: {
    badge: {
      risk: { color: '#bc0000', iconName: 'exclamationmark.triangle.fill' },
      competitiveness: { color: '#E0BC00', iconName: 'bar-chart' },
    },
    setupAndOperationalCosts: {
      resume: { value: 100, color: ResumeColor.RED },
      items: [
        { label: 'setupCost', value: 100, color: ResumeColor.RED },
        { label: 'operationalCost', value: 100, color: ResumeColor.RED },
      ],
    },
    scability: {
      resume: { value: 5, color: ResumeColor.RED },
      items: [
        { label: 'presence', value: 5, color: ResumeColor.RED },
        { label: 'scalability', value: 5, color: ResumeColor.RED },
      ],
    },
    timeToMarket: {
      resume: { value: 98, color: ResumeColor.RED },
    },
    transactionalAndPaymentCapabilities: {
      resume: { value: 80, color: ResumeColor.GREEN },
      items: [
        { label: 'localInvoicingBenefits', value: 3, color: ResumeColor.GREEN },
        { label: 'localCurrencyPayments', value: 3, color: ResumeColor.GREEN },
        { label: 'consolidation', value: 1, color: ResumeColor.RED },
      ],
    },
    customerRelationship: {
      resume: { value: 100, color: ResumeColor.GREEN },
      items: [
        { label: 'localizedContacts', value: 3, color: ResumeColor.GREEN },
        { label: 'clientRelationship', value: 3, color: ResumeColor.GREEN },
      ],
    },
    businessAutonomy: {
      resume: { value: 100, color: ResumeColor.GREEN },
      items: [
        { label: 'operationalIndependence', value: 3, color: ResumeColor.GREEN },
        { label: 'retainControl', value: 3, color: ResumeColor.GREEN },
      ],
    },
    compliance: {
      resume: { value: 100, color: ResumeColor.GREEN },
    },
  },
  alsoThere: {
    badge: {
      risk: { color: '#68BA7F', iconName: 'exclamationmark.triangle.fill' },
      competitiveness: { color: '#68BA7F', iconName: 'bar-chart' },
    },
    setupAndOperationalCosts: {
      resume: { value: 50, color: ResumeColor.YELLOW },
      items: [
        { label: 'setupCost', value: 45, color: ResumeColor.GREEN },
        { label: 'operationalCost', value: 20, color: ResumeColor.GREEN },
      ],
    },
    scability: {
      resume: { value: 50, color: ResumeColor.GREEN },
      items: [
        { label: 'presence', value: 60, color: ResumeColor.GREEN },
        { label: 'scalability', value: 40, color: ResumeColor.GREEN },
      ],
    },
    timeToMarket: {
      resume: { value: 50, color: ResumeColor.GREEN },
    },
    transactionalAndPaymentCapabilities: {
      resume: { value: 80, color: ResumeColor.GREEN },
      items: [
        { label: 'localInvoicingBenefits', value: 2, color: ResumeColor.GREEN },
        { label: 'localCurrencyPayments', value: 2, color: ResumeColor.GREEN },
        { label: 'consolidation', value: 1, color: ResumeColor.GREEN },
      ],
    },
    customerRelationship: {
      resume: { value: 30, color: ResumeColor.GREEN },
      items: [
        { label: 'localizedContacts', value: 3, color: ResumeColor.GREEN },
        { label: 'clientRelationship', value: 1, color: ResumeColor.GREEN },
      ],
    },
    businessAutonomy: {
      resume: { value: 20, color: ResumeColor.GREEN },
      items: [
        { label: 'operationalIndependence', value: 1, color: ResumeColor.GREEN },
        { label: 'retainControl', value: 1, color: ResumeColor.GREEN },
      ],
    },
    compliance: {
      resume: { value: 100, color: ResumeColor.GREEN },
    },
  },
};
